import React, { useEffect, useState } from 'react';
import api from '../services/api';
import '../styles/Admins.css';

interface Admin {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  createdAt: string;
}

interface AdminFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const Admins: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<AdminFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      const data = await api.getAdmins();
      setAdmins(data.admins);
      setError('');
    } catch (err: any) {
      setError('Failed to load admins');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (adminId: string, currentStatus: boolean) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this admin?`)) {
      return;
    }

    try {
      await api.updateAdminStatus(adminId, !currentStatus);
      fetchAdmins();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update admin status');
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    });
    setFormError('');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    });
    setFormError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) {
      setFormError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setFormError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setFormError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Please enter a valid email address');
      return false;
    }
    if (!formData.password) {
      setFormError('Password is required');
      return false;
    }
    if (formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createAdmin(formData);
      closeCreateModal();
      fetchAdmins();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to create admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (isLoading && admins.length === 0) {
    return (
      <div className="admins-page">
        <div className="loading">Loading admins...</div>
      </div>
    );
  }

  return (
    <div className="admins-page">
      <div className="page-header">
        <div>
          <h1>Admin Management</h1>
          <p>Manage admin users and permissions</p>
        </div>
        <div className="header-actions">
          <button className="btn-create-admin" onClick={openCreateModal}>
            ➕ Create New Admin
          </button>
          <button className="refresh-button" onClick={fetchAdmins}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Desktop Table */}
      <div className="desktop-table-container">
        <div className="table-container">
          <table className="admins-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Name</th>
              <th>Email</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin._id} className={!admin.isActive ? 'inactive-row' : ''}>
                <td>
                  <span className={`status-badge ${admin.isActive ? 'active' : 'inactive'}`}>
                    {admin.isActive ? '🟢 Active' : '⚫ Inactive'}
                  </span>
                </td>
                <td>
                  <strong>{admin.firstName} {admin.lastName}</strong>
                </td>
                <td>
                  <span className="email-text">{admin.email}</span>
                </td>
                <td>{formatDate(admin.createdAt)}</td>
                <td>
                  <button
                    onClick={() => handleToggleStatus(admin._id, admin.isActive)}
                    className={admin.isActive ? 'btn-deactivate' : 'btn-activate'}
                  >
                    {admin.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

          {admins.length === 0 && !isLoading && (
            <div className="empty-state">
              <p>No admins found</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-card-list">
        {admins.map((admin) => (
          <div key={admin._id} className="data-card">
            <div className="data-card-header">
              <div>
                <div className="data-card-title">
                  {admin.firstName} {admin.lastName}
                </div>
                <div className="data-card-subtitle">{admin.email}</div>
              </div>
              <span className={`status-badge ${admin.isActive ? 'active' : 'inactive'}`}>
                {admin.isActive ? '🟢' : '⚫'}
              </span>
            </div>

            <div className="data-card-body">
              <div className="data-card-row">
                <span className="data-card-label">Status:</span>
                <span className="data-card-value">
                  {admin.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Created:</span>
                <span className="data-card-value">{formatDate(admin.createdAt)}</span>
              </div>
            </div>

            <div className="data-card-actions">
              <button
                onClick={() => handleToggleStatus(admin._id, admin.isActive)}
                className={admin.isActive ? 'btn-deactivate' : 'btn-activate'}
              >
                {admin.isActive ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}

        {admins.length === 0 && !isLoading && (
          <div className="empty-state">
            <p>No admins found</p>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Admin</h2>
              <button onClick={closeCreateModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleCreateAdmin} className="admin-form">
              {formError && <div className="form-error">{formError}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="admin@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Minimum 6 characters"
                  required
                  minLength={6}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="btn-cancel-form"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit-form"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;
